import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Upload,
  Select,
  DatePicker,
  InputNumber,
  Card,
  Row,
  Col,
  message,
  Modal,
  Space,
  Divider
} from 'antd';
import {
  UploadOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  CopyOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { companyService } from '../../services/companyService';
import api from '../../services/api';

const { Option } = Select;
const { RangePicker } = DatePicker;

const AddCompany = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);

  const navigate = useNavigate();

  const fetchPackages = async () => {
    try {
      const response = await api.get('/packages?active=true');
      if (response.data.success) {
        setPackages(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      message.error('Failed to load packages');
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  // Calculate final price with GST and discount
  useEffect(() => {
    if (selectedPackage) {
      const basePrice = selectedPackage.price;
      const discountAmount = (basePrice * discountPercent) / 100;
      const discountedPrice = basePrice - discountAmount;
      const gstAmount = discountedPrice * 0.18; // 18% GST
      const total = discountedPrice + gstAmount;
      setFinalPrice(total);
    }
  }, [selectedPackage, discountPercent]);

  // Logo upload validation
  const beforeUpload = (file) => {
    const isValidType = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/gif';
    if (!isValidType) {
      message.error('You can only upload JPG/PNG/GIF files!');
      return false;
    }
    
    const isLt4M = file.size / 1024 / 1024 < 4;
    if (!isLt4M) {
      message.error('Image must be smaller than 4MB!');
      return false;
    }
    
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
    
    return false; // Prevent automatic upload
  };



  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Use email as username, and password entered manually in the form
      const username = values.email;
      const password = values.password;

      // Prepare form data
      const formData = new FormData();
      formData.append('companyName', values.companyName);
      formData.append('clientName', values.clientName);
      formData.append('phone', values.phone);
      formData.append('email', values.email);
      formData.append('username', username);
      formData.append('password', password);
      formData.append('packageId', selectedPackage.id);
      formData.append('startDate', values.dateRange[0].format('YYYY-MM-DD'));
      formData.append('expiryDate', values.dateRange[1].format('YYYY-MM-DD'));
      formData.append('discount', discountPercent);
      formData.append('finalPrice', finalPrice);
      
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      // Create company
      const response = await companyService.createCompany(formData);
      
      if (response.success) {
        message.success('Company created and credentials sent successfully!');
        navigate('/companies');
      } else {
        message.error('Failed to create company. Please try again.');
      }
    } catch (error) {
      console.error('Error creating company:', error);
      message.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  return (
    <div style={{ background: '#f5f7fa', margin: '-24px -16px', padding: '20px', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '8px' }}>
          🏠 Super Admin › Companies › <span style={{ color: '#1f2937' }}>Add New Company</span>
        </div>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Add New Company</h2>
      </div>

      <Card 
        style={{ 
          maxWidth: 900, 
          margin: '0 auto',
          borderRadius: '8px',
          border: '1px solid #f0f0f0',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          {/* Company Logo with Preview */}
          <Row gutter={16}>
            <Col span={24} style={{ marginBottom: '20px' }}>
              <div style={{ 
                padding: '20px', 
                background: '#fafafa', 
                borderRadius: '8px',
                border: '1px dashed #d9d9d9'
              }}>
                <Form.Item
                  label={<span style={{ fontWeight: '600' }}>Company Logo</span>}
                  extra="Maximum file size: 4MB. Supported formats: JPG, PNG, GIF"
                  style={{ marginBottom: 0 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <Upload
                      beforeUpload={beforeUpload}
                      showUploadList={false}
                      accept=".jpg,.jpeg,.png,.gif"
                    >
                      <Button icon={<UploadOutlined />} size="large">
                        Upload Company Logo
                      </Button>
                    </Upload>
                    
                    {logoPreview && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        padding: '12px',
                        background: 'white',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <img 
                          src={logoPreview} 
                          alt="Logo Preview" 
                          style={{ 
                            width: '80px', 
                            height: '80px', 
                            objectFit: 'contain',
                            borderRadius: '4px'
                          }} 
                        />
                        <div>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{logoFile?.name}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {(logoFile?.size / 1024).toFixed(2)} KB
                          </div>
                          <Button 
                            type="link" 
                            danger 
                            size="small" 
                            onClick={() => {
                              setLogoFile(null);
                              setLogoPreview(null);
                            }}
                            style={{ padding: 0, height: 'auto', marginTop: '4px' }}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </Form.Item>
              </div>
            </Col>
          </Row>

          <Divider style={{ margin: '24px 0' }} />

          {/* Basic Information */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
              Basic Information
            </h3>
            <Row gutter={16}>
              {/* Company Name */}
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={{ fontWeight: '500' }}>Company Name</span>}
                  name="companyName"
                  rules={[
                    { required: true, message: 'Please enter company name!' },
                    { min: 2, message: 'Company name must be at least 2 characters!' }
                  ]}
                >
                  <Input placeholder="Enter company name" size="large" />
                </Form.Item>
              </Col>

              {/* Client Name */}
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={{ fontWeight: '500' }}>Client Name</span>}
                  name="clientName"
                  rules={[
                    { required: true, message: 'Please enter client name!' },
                    { min: 2, message: 'Client name must be at least 2 characters!' }
                  ]}
                >
                  <Input placeholder="Enter client name" size="large" />
                </Form.Item>
              </Col>

              {/* Phone Number */}
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={{ fontWeight: '500' }}>Phone Number</span>}
                  name="phone"
                  rules={[
                    { required: true, message: 'Please enter phone number!' },
                    { pattern: /^[6-9]\d{9}$/, message: 'Please enter valid phone number!' }
                  ]}
                >
                  <Input placeholder="Enter phone number" maxLength={10} size="large" />
                </Form.Item>
              </Col>

              {/* Email Address */}
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={{ fontWeight: '500' }}>Email Address</span>}
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter email address!' },
                    { type: 'email', message: 'Please enter valid email address!' }
                  ]}
                >
                  <Input placeholder="Enter email address" size="large" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Divider style={{ margin: '24px 0' }} />

          {/* Account Settings */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
              Account Settings
            </h3>
            <Row gutter={16}>
              {/* Admin Password - Now md={12} */}
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={{ fontWeight: '500' }}>Admin Password</span>}
                  name="password"
                  rules={[
                    { required: true, message: 'Please enter admin password!' },
                    { min: 8, message: 'Password must be at least 8 characters!' }
                  ]}
                >
                  <Input.Password 
                    placeholder="Create admin password (min 8 characters)" 
                    size="large"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>
              </Col>

              {/* Package Selection - Now md={12} */}
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={{ fontWeight: '500' }}>Package Selection</span>}
                  name="packageId"
                  rules={[{ required: true, message: 'Please select a package!' }]}
                >
                  <Select
                    placeholder="Select package"
                    size="large"
                    onChange={(value) => {
                      const pkg = packages.find(p => p.id === value);
                      setSelectedPackage(pkg);
                    }}
                  >
                    {packages.map(pkg => (
                      <Option key={pkg.id} value={pkg.id}>
                        <div>
                          <strong>{pkg.name}</strong> - ₹{pkg.price.toLocaleString()} + GST
                          <br />
                          <small style={{ color: '#6b7280' }}>
                            {pkg.description} ({pkg.userLimit} users)
                          </small>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Divider style={{ margin: '24px 0' }} />

          {/* Subscription Details */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
              Subscription Details
            </h3>
            <Row gutter={16}>
              {/* Date Range */}
              <Col xs={24} md={16}>
                <Form.Item
                  label={<span style={{ fontWeight: '500' }}>Service Period</span>}
                  name="dateRange"
                  rules={[{ required: true, message: 'Please select service period!' }]}
                >
                  <RangePicker
                    style={{ width: '100%' }}
                    size="large"
                    disabledDate={(current) => current && current < moment().startOf('day')}
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>

              {/* Discount */}
              <Col xs={24} md={8}>
                <Form.Item
                  label={<span style={{ fontWeight: '500' }}>Discount (%)</span>}
                  name="discount"
                >
                  <InputNumber
                    min={0}
                    max={50}
                    placeholder="0"
                    size="large"
                    style={{ width: '100%' }}
                    onChange={setDiscountPercent}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Price Summary */}
          {selectedPackage && (
            <Card 
              size="small" 
              style={{ 
                marginBottom: 20, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <Row gutter={16} align="middle">
                <Col span={16}>
                  <div style={{ color: 'white' }}>
                    <div style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>Price Breakdown</div>
                    <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                      Base Price: ₹{selectedPackage.price.toLocaleString()}
                    </div>
                    {discountPercent > 0 && (
                      <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                        Discount ({discountPercent}%): -₹{((selectedPackage.price * discountPercent) / 100).toLocaleString()}
                      </div>
                    )}
                    <div style={{ fontSize: '14px' }}>
                      GST (18%): +₹{((selectedPackage.price - (selectedPackage.price * discountPercent / 100)) * 0.18).toLocaleString()}
                    </div>
                  </div>
                </Col>
                <Col span={8} style={{ textAlign: 'right' }}>
                  <div style={{ color: 'white' }}>
                    <div style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>Final Amount</div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                      ₹{Math.round(finalPrice).toLocaleString()}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          )}

          {/* Submit Buttons */}
          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space size="middle">
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading} 
                size="large"
                style={{ 
                  minWidth: '150px',
                  backgroundColor: '#ff6b35',
                  borderColor: '#ff6b35'
                }}
              >
                Create Company
              </Button>
              <Button 
                onClick={() => navigate('/companies')} 
                size="large"
                style={{ minWidth: '120px' }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default AddCompany;